import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Snackbar,
} from '@mui/material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { Upload, Download } from '@mui/icons-material';

// PDF files are in public/assets directory
const abstractGuidelines = '/assets/Abstract Guidelines & Format.pdf';
const abstractTemplate = '/assets/First name_surname_place_short title.pdf';

const validationSchema = Yup.object({
  name: Yup.string().required('Name is required'),
  email: Yup.string().email('Invalid email address').required('Email is required'),
  title: Yup.string().required('Title is required'),
  file: Yup.mixed()
    .required('File is required')
    .test('fileSize', 'File size is too large', (value) => {
      if (!value) return true;
      return value.size <= 5000000; // 5MB limit
    })
    .test('fileType', 'Only PDF files are allowed', (value) => {
      if (!value) return true;
      return value.type === 'application/pdf';
    }),
});

const Abstract = () => {
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadError, setUploadError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (values, { resetForm }) => {
    setUploading(true);
    setUploadError(false);
    setUploadSuccess(false);

    try {
      // Create form data
      const formData = new FormData();
      formData.append('file', values.file);
      formData.append('name', values.name);
      formData.append('email', values.email);
      formData.append('title', values.title);

      console.log('Attempting to upload file...', {
        name: values.name,
        email: values.email,
        title: values.title,
        fileName: values.file.name
      });

      // Upload to backend API (now configured for Google Drive)
      const response = await fetch('https://conference-kv80.onrender.com/api/upload-to-drive', {
        method: 'POST',
        body: formData,
        headers: {
          // Don't set Content-Type header - it will be set automatically with the correct boundary
          // when sending FormData
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        console.error('Upload failed:', {
          status: response.status,
          statusText: response.statusText,
          errorData
        });
        throw new Error(
          errorData?.details || 
          errorData?.error || 
          `Upload failed with status ${response.status}`
        );
      }

      const result = await response.json();
      console.log('Upload successful:', result);
      setUploadSuccess(true);
      resetForm();
      
    } catch (error) {
      console.error('Upload error:', error);
      setUploadError(true);
      setErrorMessage(
        error.message === 'Failed to fetch' 
          ? 'Unable to connect to server. Please make sure the server is running.'
          : error.message || 'Failed to upload abstract'
      );
    } finally {
      setUploading(false);
    }
  };

  return (
    <Container maxWidth="md">
      <Box sx={{ py: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Abstract Submission
        </Typography>
        <Typography variant="subtitle1" gutterBottom align="center" color="text.secondary">
          Submit your abstract for the conference
        </Typography>

        <Paper sx={{ p: 4, mt: 4 }}>
          <Formik
            initialValues={{
              name: '',
              email: '',
              title: '',
              file: null,
            }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ values, errors, touched, handleChange, handleBlur, setFieldValue }) => (
              <Form>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      name="name"
                      label="Full Name"
                      value={values.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      name="email"
                      label="Email"
                      type="email"
                      value={values.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.email && Boolean(errors.email)}
                      helperText={touched.email && errors.email}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      name="title"
                      label="Abstract Title"
                      value={values.title}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.title && Boolean(errors.title)}
                      helperText={touched.title && errors.title}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Button
                        variant="outlined"
                        component="label"
                        startIcon={<Upload />}
                      >
                        Upload PDF
                        <input
                          type="file"
                          hidden
                          accept=".pdf"
                          onChange={(event) => {
                            setFieldValue('file', event.currentTarget.files[0]);
                          }}
                        />
                      </Button>
                      {values.file && (
                        <Typography variant="body2" color="text.secondary">
                          Selected file: {values.file.name}
                        </Typography>
                      )}
                    </Box>
                    {touched.file && errors.file && (
                      <Typography color="error" variant="caption">
                        {errors.file}
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      fullWidth
                      size="large"
                      disabled={uploading}
                    >
                      {uploading ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        'Submit Abstract'
                      )}
                    </Button>
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>
        </Paper>
      </Box>

      <Snackbar
        open={uploadSuccess}
        autoHideDuration={6000}
        onClose={() => setUploadSuccess(false)}
      >
        <Alert severity="success" sx={{ width: '100%' }}>
          Abstract submitted successfully!
        </Alert>
      </Snackbar>

      <Snackbar
        open={uploadError}
        autoHideDuration={6000}
        onClose={() => setUploadError(false)}
      >
        <Alert severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>

      

      {/* Abstract Guidelines Section */}
      <Box sx={{ mt: 6 }}>
        <Paper sx={{ p: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom align="center">
            Abstract Submission Guidelines
          </Typography>
          <Typography variant="body1" paragraph align="center" color="text.secondary">
            Please download and review the following guidelines before submitting your abstract
          </Typography>
          
          <Grid container spacing={3} justifyContent="center" sx={{ mt: 2 }}>
            <Grid item xs={12} sm={6} md={4}>
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                startIcon={<Download />}
                onClick={() => window.open(abstractGuidelines, '_blank')}
                sx={{ py: 2 }}
              >
                Abstract Format Guidelines
              </Button>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                startIcon={<Download />}
                onClick={() => window.open(abstractTemplate, '_blank')}
                sx={{ py: 2 }}
              >
                Poster Template
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </Container>
  );
};

export default Abstract; 